import {
    type AgolFieldInfo,
    type AgolLayerInfo,
    type CodedValueInfo,
    type FieldMapping,
    type FieldMappingProfile,
    type ForvaltningsFieldDataType,
    LOKAL_ID_SOURCE_PATH,
    type PreflightReport,
    type ValueMapping,
    UnmappedValuePolicy,
} from '../../../api/types';

/** Redigerbar tilstand for ett felt i veiviseren. */
export interface FieldDraft {
    ngisSourcePath: string;
    /** AGOL-feltnavn; tom streng = ikke mappet (utelates fra profilen). */
    agolFieldName: string;
    dataType: ForvaltningsFieldDataType;
    isDateOnly: boolean;
    matchKind: string;
    typeCompatible: boolean;
    /** Redigerbare verdimap-par, eller null for fritekst/tall-felt. */
    valueMap: ValueMapping[] | null;
    unmatchedCodes: string[] | null;
    onUnmapped: UnmappedValuePolicy;
    /** NGIS-feltets kodeliste, eller null hvis feltet ikke har domene (eller API-et ikke leverer det). */
    ngisCodedValues: CodedValueInfo[] | null;
}

/** Redigerbar tilstand for ett AGOL-lag. */
export interface LayerDraft {
    /** -1 = ikke koblet til noe AGOL-lag (utelates fra profilen). */
    agolLayerId: number;
    agolLayerName: string;
    /** Esri-geometritypen til det koblede AGOL-laget; tom når ikke koblet eller ukjent. */
    agolGeometryType: string;
    featureType: string;
    /** NGIS-geometrien (Punkt/Linje/Flate). Objekttype + geometri er nøkkelen — ikke objekttype alene. */
    geometryKind: string;
    agolObjectIdField: string;
    agolGlobalIdField: string;
    editorField?: string | null;
    /** Felt i det valgte AGOL-laget (for nedtrekk); tom når ikke koblet. */
    availableAgolFields: AgolFieldInfo[];
    fields: FieldDraft[];
}

export type LayerStatus = 'ready' | 'needsLokalId' | 'unassigned';

/**
 * Geometrifamilie (point/line/polygon) fra NGIS-norsk (Punkt/Linje/Flate) eller Esri-form
 * (esriGeometryPoint/Polyline/Polygon). Speiler `GeometryFamily` i forvaltning-api.
 */
export function geometryFamily(kind: string | null | undefined): string {
    const t = (kind ?? '').toLowerCase();
    if (t.includes('point') || t.includes('punkt')) return 'point';
    if (t.includes('line') || t.includes('linje') || t.includes('kurve') || t.includes('curve')) return 'line';
    if (t.includes('polygon') || t.includes('flate') || t.includes('surface')) return 'polygon';
    return t;
}

/** True når begge er kjente og i samme geometrifamilie. */
export function sameGeometry(a: string | null | undefined, b: string | null | undefined): boolean {
    const fa = geometryFamily(a);
    return fa.length > 0 && fa === geometryFamily(b);
}

/**
 * Nøkkelen en objekttype-variant identifiseres med. Hver geometri av samme objekttype har sitt
 * eget AGOL-sublag, så objekttype alene kolliderer (Hendelse Punkt vs Hendelse Flate).
 */
function variantKey(featureType: string, geometryKind: string): string {
    return `${featureType.toLowerCase()}|${geometryFamily(geometryKind)}`;
}

/** Kort beskrivelse av en variant til meldinger: «Hendelse (Flate)». */
export function describeVariant(layer: Pick<LayerDraft, 'featureType' | 'geometryKind'>): string {
    return `${layer.featureType} (${layer.geometryKind})`;
}

/** Slår opp et inspisert AGOL-lag på id. */
function findAgolLayer(layers: AgolLayerInfo[], id: number): AgolLayerInfo | undefined {
    return layers.find((l) => l.id === id);
}

/** Companion-tabellen får dette suffikset (AgolFeatureStatusViewPublisher). */
const COMPANION_TABLE_SUFFIX = '_synkstatus';

/**
 * Lag som faktisk kan kobles til en NGIS-objekttype: kun romlige lag.
 * Skjuler companion-/status-tabeller — de er ikke-romlige tabeller (tom geometritype)
 * og bærer `_synkstatus`-suffikset; en objekttype kan aldri mappes mot dem.
 */
export function mappableAgolLayers(layers: AgolLayerInfo[]): AgolLayerInfo[] {
    return layers.filter((l) => {
        const geom = (l.geometryType ?? '').toLowerCase();
        const spatial = geom.length > 0 && geom !== 'esrigeometrynull' && geom !== 'esrigeometrynone';
        const isCompanion = l.name.toLowerCase().endsWith(COMPANION_TABLE_SUFFIX);
        return spatial && !isCompanion;
    });
}

/** Bygger redigerbart utkast fra preflight-rapporten. */
export function draftFromReport(report: PreflightReport): LayerDraft[] {
    const agolLayers = report.availableAgolLayers ?? [];
    return report.layers.map((layer) => {
        const matched = findAgolLayer(agolLayers, layer.agolLayerId);
        return {
            agolLayerId: layer.agolLayerId,
            agolLayerName: layer.agolLayerName,
            agolGeometryType: matched?.geometryType ?? '',
            featureType: layer.featureType,
            geometryKind: layer.geometryKind,
            agolObjectIdField: layer.agolObjectIdField,
            agolGlobalIdField: layer.agolGlobalIdField,
            editorField: layer.editorField,
            availableAgolFields: matched?.fields ?? [],
            fields: layer.fields.map((f) => ({
                ngisSourcePath: f.ngisSourcePath,
                agolFieldName: f.agolFieldName ?? '',
                dataType: f.dataType,
                isDateOnly: f.isDateOnly,
                matchKind: f.matchKind,
                typeCompatible: f.typeCompatible,
                valueMap: f.valueMap
                    ? f.valueMap.map((v) => ({ agolValue: v.agolValue, ngisValue: v.ngisValue }))
                    : null,
                unmatchedCodes: f.unmatchedCodes ?? null,
                // Kodeliste-felt anbefales å feile på ukjent verdi; fritekst sendes uendret.
                onUnmapped: f.valueMap ? UnmappedValuePolicy.Fail : UnmappedValuePolicy.PassThrough,
                ngisCodedValues: f.ngisCodedValues?.length ? f.ngisCodedValues : null,
            })),
        };
    });
}

/** True hvis NGIS-feltet har kodeliste, eller preflight allerede har foreslått en verdimap. */
export function hasDomain(field: FieldDraft): boolean {
    return (field.ngisCodedValues?.length ?? 0) > 0 || field.valueMap !== null;
}

// Bytter æ/ø/å, fjerner øvrige diakritiske tegn (é→e) og alt som ikke er bokstav/siffer.
function foldNorwegian(value: string, ae: string, oe: string, aa: string): string {
    return value
        .replace(/æ/g, ae)
        .replace(/ø/g, oe)
        .replace(/å/g, aa)
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .replace(/[^\p{L}\p{N}]/gu, '');
}

/**
 * Sammenligningsnøkler som tåler ulik skriving av norske tegn (`hoy`/`hoey` ↔ «Høy»,
 * `pagaende`/`paagaaende` ↔ «Pågående»). Bevisst kun én vei: `oe`/`aa` tolkes aldri som ø/å.
 * Speiler `CodeValueKey` i forvaltning-api.
 */
export function codeValueVariants(value: string | null | undefined): Set<string> {
    const keys = new Set<string>();
    if (!value?.trim()) return keys;
    const lower = value.toLowerCase();
    for (const key of [foldNorwegian(lower, 'ae', 'o', 'a'), foldNorwegian(lower, 'ae', 'oe', 'aa')]) {
        if (key) keys.add(key);
    }
    return keys;
}

function codeValuesMatch(a: string | null | undefined, b: string | null | undefined): boolean {
    const va = codeValueVariants(a);
    for (const key of codeValueVariants(b)) if (va.has(key)) return true;
    return false;
}

// 0: likt navn. 1: navn like etter bretting av æøå. 2: kode mot navn (begge veier). 3: kode mot kode.
function valueMatchTier(ngis: CodedValueInfo, agol: CodedValueInfo): number | null {
    if (ngis.name.toLowerCase() === agol.name.toLowerCase()) return 0;
    if (codeValuesMatch(ngis.name, agol.name)) return 1;
    if (codeValuesMatch(ngis.code, agol.name) || codeValuesMatch(ngis.name, agol.code)) return 2;
    if (codeValuesMatch(ngis.code, agol.code)) return 3;
    return null;
}

/**
 * Foreslår verdimap ved å matche kodelistene: likt navn først, deretter med æøå-varianter og
 * kode mot navn. Hver NGIS- og AGOL-verdi brukes maks én gang, sikreste treff først.
 * Speiler `PreflightMatcher.SuggestValueMap` i forvaltning-api.
 */
export function suggestValueMap(
    ngisCodes: CodedValueInfo[],
    agolCodes: CodedValueInfo[],
): { valueMap: ValueMapping[]; unmatchedCodes: string[] | null } {
    const candidates: { ngis: number; agol: number; tier: number }[] = [];
    for (const [i, ngis] of ngisCodes.entries()) {
        for (const [j, agol] of agolCodes.entries()) {
            const tier = valueMatchTier(ngis, agol);
            if (tier !== null) candidates.push({ ngis: i, agol: j, tier });
        }
    }
    candidates.sort((a, b) => a.tier - b.tier || a.ngis - b.ngis || a.agol - b.agol);

    const assigned = new Map<number, CodedValueInfo>();
    const usedAgol = new Set<number>();
    for (const c of candidates) {
        if (assigned.has(c.ngis) || usedAgol.has(c.agol)) continue;
        assigned.set(c.ngis, agolCodes[c.agol]);
        usedAgol.add(c.agol);
    }

    const valueMap: ValueMapping[] = [];
    const unmatched: string[] = [];
    for (const [i, ngis] of ngisCodes.entries()) {
        const agol = assigned.get(i);
        if (agol) valueMap.push({ agolValue: agol.code, ngisValue: ngis.code });
        else unmatched.push(ngis.name);
    }
    return { valueMap, unmatchedCodes: unmatched.length > 0 ? unmatched : null };
}

/** Slår opp et AGOL-felt på navn (case-insensitivt). */
export function findAgolField(fields: AgolFieldInfo[], name: string): AgolFieldInfo | undefined {
    const key = name.trim().toLowerCase();
    return key ? fields.find((f) => f.name.toLowerCase() === key) : undefined;
}

/**
 * Kobler et NGIS-felt til et AGOL-felt og regner ut verdimap-forslaget på nytt for kodeliste-felt.
 * Forslaget fra preflight gjelder bare feltet preflight selv matchet — byttes lag eller felt, må
 * det bygges på nytt fra kodelistene. Har AGOL-feltet ingen kodeliste, starter verdimapen tom så
 * brukeren kan legge inn par selv. Felt uten NGIS-kodeliste (eller fra et eldre API) røres ikke.
 */
export function withAgolField(
    field: FieldDraft,
    agolFieldName: string,
    agolFields: AgolFieldInfo[],
): FieldDraft {
    const next = { ...field, agolFieldName };
    const ngisCodes = field.ngisCodedValues;
    if (!ngisCodes?.length || !agolFieldName.trim()) return next;

    const agolCodes = findAgolField(agolFields, agolFieldName)?.codedValues;
    if (!agolCodes?.length) {
        return { ...next, valueMap: [], unmatchedCodes: null, onUnmapped: UnmappedValuePolicy.PassThrough };
    }
    const { valueMap, unmatchedCodes } = suggestValueMap(ngisCodes, agolCodes);
    return {
        ...next,
        valueMap,
        unmatchedCodes,
        onUnmapped: valueMap.length > 0 ? UnmappedValuePolicy.Fail : UnmappedValuePolicy.PassThrough,
    };
}

/**
 * Kobler en objekttype til et annet AGOL-lag (eller til ingenting).
 * Beholder feltmappinger der AGOL-feltnavnet også finnes i det nye laget, og
 * tømmer resten. NGIS-felt-radene bevares; verdimap-forslag regnes ut på nytt mot
 * det nye lagets kodelister.
 */
export function reassignLayer(layer: LayerDraft, target: AgolLayerInfo | null): LayerDraft {
    if (!target) {
        return {
            ...layer,
            agolLayerId: -1,
            agolLayerName: '',
            agolGeometryType: '',
            availableAgolFields: [],
            fields: layer.fields.map((f) => ({ ...f, agolFieldName: '' })),
        };
    }
    const fieldNames = new Set(target.fields.map((f) => f.name.toLowerCase()));
    return {
        ...layer,
        agolLayerId: target.id,
        agolLayerName: target.name,
        agolGeometryType: target.geometryType,
        agolObjectIdField: target.objectIdField,
        agolGlobalIdField: target.globalIdField,
        availableAgolFields: target.fields,
        fields: layer.fields.map((f) =>
            withAgolField(
                f,
                fieldNames.has(f.agolFieldName.trim().toLowerCase()) ? f.agolFieldName : '',
                target.fields,
            ),
        ),
    };
}

/**
 * Overlegger en lagret mapping-profil på et ferskt preflight-utkast.
 *
 * Brukes ved redigering av en eksisterende kobling: preflight gir oss dagens
 * NGIS-felt, AGOL-lag/-felt og verdimap-forslag, mens den lagrede profilen er
 * fasit for brukerens tidligere valg. Resultatet forhåndsutfyller lag-kobling,
 * feltmapping, verdimap og policy slik de ble lagret — men med nedtrekk og
 * forslag som speiler tjenesten/skjemaet slik det ser ut nå.
 *
 * - Profilen slås opp på objekttype + geometri. Oppslag på objekttype alene lot den siste
 *   varianten (f.eks. Tiltaksområde Linje) overskrive de andre ved lagring.
 * - Objekttyper som ikke fantes i profilen starter ukoblet.
 * - Felt som ikke var mappet i profilen vises umappet (profilen er fasit, ikke
 *   preflight-forslaget), men forslaget er fortsatt valgbart i nedtrekket.
 * - NGIS-felt som ikke lenger finnes i skjemaet faller naturlig bort.
 */
export function applyProfileToDraft(
    draft: LayerDraft[],
    profile: FieldMappingProfile | null,
    availableAgolLayers: AgolLayerInfo[],
): LayerDraft[] {
    if (!profile) return draft;
    const savedByVariant = new Map(
        profile.layers.map((l) => [variantKey(l.featureType, l.geometryKind), l]),
    );
    return draft.map((layer) => {
        const saved = savedByVariant.get(variantKey(layer.featureType, layer.geometryKind));
        if (!saved) return reassignLayer(layer, null);

        // Pek laget på det AGOL-laget profilen brukte (henter riktige felt-nedtrekk).
        const target = findAgolLayer(availableAgolLayers, saved.agolLayerId) ?? null;
        const assigned = target ? reassignLayer(layer, target) : layer;
        const savedFields = new Map(saved.fields.map((f) => [f.ngisSourcePath, f]));

        return {
            ...assigned,
            // Bevar identitetsfeltene fra profilen selv om tjenesten ikke lister laget.
            agolLayerId: saved.agolLayerId,
            agolObjectIdField: saved.agolObjectIdField || assigned.agolObjectIdField,
            agolGlobalIdField: saved.agolGlobalIdField || assigned.agolGlobalIdField,
            fields: assigned.fields.map((f) => {
                const sf = savedFields.get(f.ngisSourcePath);
                // Ikke mappet i profilen → vis umappet (forslaget er fortsatt valgbart).
                if (!sf) return { ...f, agolFieldName: '' };
                return {
                    ...f,
                    agolFieldName: sf.agolFieldName ?? '',
                    valueMap:
                        sf.valueMap && sf.valueMap.length > 0
                            ? sf.valueMap.map((v) => ({ agolValue: v.agolValue, ngisValue: v.ngisValue }))
                            : f.valueMap,
                    onUnmapped: sf.onUnmapped ?? f.onUnmapped,
                };
            }),
        };
    });
}

/** True hvis laget er koblet til et AGOL-lag. */
export function isLayerAssigned(layer: LayerDraft): boolean {
    return layer.agolLayerId >= 0;
}

/** AGOL-feltnavnet et lag mapper identifikasjon.lokalId til, eller null. */
export function lokalIdFieldOf(layer: LayerDraft): string | null {
    const mapped = layer.fields.find((f) => f.ngisSourcePath === LOKAL_ID_SOURCE_PATH);
    return mapped?.agolFieldName.trim() || null;
}

/**
 * LokalId-feltet som lagres på bindingen (`SyncBinding.lokalIdField`). Utledes fra mappingen
 * i stedet for å spørres om separat — API-et bruker det som AGOL-kolonnenavn når det slår opp
 * en feature på lokalId, så det MÅ være samme felt som profilen mapper. Tas fra første koblede
 * lag; faller tilbake på 'lokalId' hvis ingen lag er koblet ennå.
 */
export function resolveLokalIdField(layers: LayerDraft[]): string {
    for (const layer of layers.filter(isLayerAssigned)) {
        const field = lokalIdFieldOf(layer);
        if (field) return field;
    }
    return 'lokalId';
}

/** True hvis laget har en ikke-tom mapping for identifikasjon.lokalId. */
export function layerHasLokalId(layer: LayerDraft): boolean {
    return lokalIdFieldOf(layer) !== null;
}

/** Status for ett lag, brukt til badge i venstre liste. */
export function layerStatus(layer: LayerDraft): LayerStatus {
    if (!isLayerAssigned(layer)) return 'unassigned';
    return layerHasLokalId(layer) ? 'ready' : 'needsLokalId';
}

/** AGOL-lag-id → variant som bruker laget, for alle koblede lag unntatt `exceptIndex`. */
export function layersUsedBy(layers: LayerDraft[], exceptIndex: number | null): Map<number, string> {
    const used = new Map<number, string>();
    for (const [i, layer] of layers.entries()) {
        if (i !== exceptIndex && isLayerAssigned(layer)) used.set(layer.agolLayerId, describeVariant(layer));
    }
    return used;
}

/**
 * Første brudd på premisset «én objekttype+geometri ↔ ett AGOL-lag», eller null. Speiler
 * `LayerAssignmentValidator` i forvaltning-api, som avviser samme profil ved lagring.
 */
export function mappingProblem(layers: LayerDraft[]): string | null {
    const byLayerId = new Map<number, LayerDraft[]>();
    for (const layer of layers.filter(isLayerAssigned)) {
        byLayerId.set(layer.agolLayerId, [...(byLayerId.get(layer.agolLayerId) ?? []), layer]);
    }
    for (const [id, owners] of byLayerId) {
        if (owners.length > 1) {
            return (
                `AGOL-lag ${owners[0].agolLayerName || id} (id ${id}) er koblet til flere objekttyper: ` +
                `${owners.map(describeVariant).join(', ')}. Hver objekttype og geometri må ha sitt eget lag.`
            );
        }
    }

    const mismatch = layers.find(
        (l) => isLayerAssigned(l) && l.agolGeometryType && !sameGeometry(l.geometryKind, l.agolGeometryType),
    );
    if (mismatch) {
        return (
            `${describeVariant(mismatch)} er koblet til ${mismatch.agolLayerName} (id ${mismatch.agolLayerId}), ` +
            `som har geometritype ${mismatch.agolGeometryType.replace(/^esriGeometry/, '')}.`
        );
    }

    return null;
}

/**
 * True når mappingen kan lagres: minst ett koblet lag, hvert koblet lag har
 * identifikasjon.lokalId mappet, og ingen lag-konflikter (se {@link mappingProblem}).
 * Ikke-koblede lag utelates og blokkerer ikke.
 */
export function mappingComplete(layers: LayerDraft[]): boolean {
    const assigned = layers.filter(isLayerAssigned);
    return assigned.length > 0 && assigned.every(layerHasLokalId) && mappingProblem(layers) === null;
}

/** Setter sammen en lagrbar mapping-profil (utelater ikke-koblede lag + felt uten AGOL-treff). */
export function buildMappingProfile(bindingId: string, layers: LayerDraft[]): FieldMappingProfile {
    return {
        bindingId,
        layers: layers.filter(isLayerAssigned).map((layer) => ({
            agolLayerId: layer.agolLayerId,
            featureType: layer.featureType,
            geometryKind: layer.geometryKind,
            agolObjectIdField: layer.agolObjectIdField,
            agolGlobalIdField: layer.agolGlobalIdField,
            fields: layer.fields
                .filter((f) => f.agolFieldName.trim().length > 0)
                .map<FieldMapping>((f) => ({
                    ngisSourcePath: f.ngisSourcePath,
                    agolFieldName: f.agolFieldName.trim(),
                    dataType: f.dataType,
                    isDateOnly: f.isDateOnly || undefined,
                    valueMap: f.valueMap && f.valueMap.length > 0 ? f.valueMap : null,
                    onUnmapped: f.onUnmapped,
                })),
        })),
    };
}
